import { X, TrendingUp, TrendingDown, AlertTriangle, Trophy, Target, Package } from 'lucide-react';
import { useEffect } from 'react';
import type { Insight } from '@/types/reports';

interface InsightsModalProps {
    isOpen: boolean;
    onClose: () => void;
    insights: Insight[];
}

export function InsightsModal({ isOpen, onClose, insights }: InsightsModalProps) {
    // Bloquear scroll quando modal aberto
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    if (!isOpen) return null;

    const getIcon = (type: string) => {
        switch (type) {
            case 'top_performers':
                return <Trophy className="w-5 h-5 text-gray-700" />;
            case 'significant_changes':
                return <AlertTriangle className="w-5 h-5 text-gray-700" />;
            case 'low_performance':
                return <AlertTriangle className="w-5 h-5 text-gray-700" />;
            case 'high_performers':
                return <TrendingUp className="w-5 h-5 text-gray-700" />;
            case 'engagement':
                return <Target className="w-5 h-5 text-gray-700" />;
            case 'new_items':
                return <Package className="w-5 h-5 text-gray-700" />;
            case 'disappeared_items':
                return <TrendingDown className="w-5 h-5 text-gray-700" />;
            default:
                return <Target className="w-5 h-5 text-gray-700" />;
        }
    };

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
                onClick={onClose}
            />

            {/* Modal */}
            <div className="flex min-h-full items-center justify-center p-4">
                <div className="relative w-full max-w-4xl bg-white rounded-xl shadow-2xl">
                    {/* Header */}
                    <div className="flex items-center justify-between p-6 border-b border-gray-200">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-gray-100 rounded-lg">
                                <Target className="w-6 h-6 text-gray-700" />
                            </div>
                            <div>
                                <h2 className="text-2xl font-bold text-gray-900">Insights Automáticos</h2>
                                <p className="text-gray-600 text-sm mt-1">
                                    {insights.length} {insights.length === 1 ? 'insight encontrado' : 'insights encontrados'}
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            <X className="w-6 h-6" />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
                        {insights.length === 0 ? (
                            <div className="text-center py-12">
                                <Target className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                                <p className="text-gray-500 text-lg font-medium">Nenhum insight disponível</p>
                                <p className="text-gray-400 text-sm mt-2">
                                    Ajuste os filtros ou período para gerar insights
                                </p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {insights.map((insight, index) => (
                                    <div
                                        key={index}
                                        className="p-4 rounded-lg border border-gray-200 bg-white hover:shadow-md transition-shadow animate-fadeIn"
                                        style={{ animationDelay: `${index * 50}ms` }}
                                    >
                                        {/* Header do Insight */}
                                        <div className="flex items-start gap-3 mb-3 pb-3 border-b border-gray-100">
                                            <div className="mt-0.5">{getIcon(insight.type)}</div>
                                            <div className="flex-1">
                                                <h3 className="font-semibold text-gray-900 text-sm">{insight.title}</h3>
                                                {insight.count && (
                                                    <p className="text-xs text-gray-500 mt-1">
                                                        {insight.count} {insight.count === 1 ? 'item' : 'itens'}
                                                        {insight.percentage && ` (${insight.percentage}%)`}
                                                    </p>
                                                )}
                                            </div>
                                        </div>

                                        {/* Conteúdo do Insight */}
                                        {insight.type === 'top_performers' && insight.items && (
                                            <div className="space-y-2">
                                                {insight.items.map((item: any, idx: number) => (
                                                    <div key={idx} className="flex items-center justify-between text-sm py-1">
                                                        <span className="text-gray-700 truncate flex-1">
                                                            {idx + 1}. {item.name}
                                                        </span>
                                                        <div className="flex items-center gap-2">
                                                            <span className="font-semibold text-gray-900">
                                                                $ {item.value.toFixed(2)}
                                                            </span>
                                                            <span className="text-xs text-gray-500">({item.percentage}%)</span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {insight.type === 'significant_changes' && insight.items && (
                                            <div className="space-y-2">
                                                {insight.items.slice(0, 5).map((item: any, idx: number) => (
                                                    <div
                                                        key={idx}
                                                        className="p-2 bg-gray-50 rounded border border-gray-200 text-sm"
                                                    >
                                                        <div className="flex items-center justify-between mb-1">
                                                            <span className="font-medium text-gray-900 truncate flex-1">
                                                                {item.name}
                                                            </span>
                                                            <span
                                                                className={`text-xs font-semibold ${
                                                                    item.trend === 'up' ? 'text-green-600' : 'text-red-600'
                                                                }`}
                                                            >
                                                                {item.change_pct > 0 ? '+' : ''}
                                                                {item.change_pct.toFixed(1)}%
                                                            </span>
                                                        </div>
                                                        <div className="flex items-center justify-between text-xs text-gray-600">
                                                            <span>
                                                                {item.trend === 'up' ? '↑' : '↓'} ${' '}
                                                                {Math.abs(item.change_abs).toFixed(2)}
                                                            </span>
                                                            <span>
                                                                $ {item.previous.toFixed(2)} → $ {item.current.toFixed(2)}
                                                            </span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {insight.type === 'low_performance' && insight.items && (
                                            <div className="space-y-1 max-h-32 overflow-y-auto">
                                                {insight.items.map((item: string, idx: number) => (
                                                    <div key={idx} className="text-sm text-gray-700 truncate py-0.5">
                                                        • {item}
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {insight.type === 'high_performers' && insight.items && (
                                            <div className="space-y-2">
                                                <div className="text-xs text-gray-500 mb-2 pb-2 border-b border-gray-100">
                                                    eCPM médio: $ {insight.avg_ecpm?.toFixed(2)}
                                                </div>
                                                {insight.items.map((item: any, idx: number) => (
                                                    <div key={idx} className="flex items-center justify-between text-sm py-1">
                                                        <span className="text-gray-700 truncate flex-1">{item.name}</span>
                                                        <span className="font-semibold text-gray-900">
                                                            $ {item.ecpm.toFixed(2)}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {insight.type === 'engagement' && insight.items && (
                                            <div className="space-y-2">
                                                <div className="text-xs text-gray-500 mb-2 pb-2 border-b border-gray-100">
                                                    CTR médio: {insight.avg_ctr?.toFixed(2)}%
                                                </div>
                                                {insight.items.map((item: any, idx: number) => (
                                                    <div key={idx} className="flex items-center justify-between text-sm py-1">
                                                        <span className="text-gray-700 truncate flex-1">{item.name}</span>
                                                        <span className="font-semibold text-gray-900">
                                                            {item.ctr.toFixed(2)}%
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {(insight.type === 'new_items' || insight.type === 'disappeared_items') &&
                                            insight.items && (
                                                <div className="space-y-1 max-h-32 overflow-y-auto">
                                                    {insight.items.map((item: string, idx: number) => (
                                                        <div key={idx} className="text-sm text-gray-700 truncate py-0.5">
                                                            • {item}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50 rounded-b-xl">
                        <button
                            onClick={onClose}
                            className="px-6 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 transition-colors"
                        >
                            Fechar
                        </button>
                    </div>
                </div>
            </div>

            {/* Animação fadeIn */}
            <style>{`
                @keyframes fadeIn {
                    from {
                        opacity: 0;
                        transform: translateY(10px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                .animate-fadeIn {
                    animation: fadeIn 0.3s ease-out forwards;
                }
            `}</style>
        </div>
    );
}